import { useMemo } from "react";

import { mergeClassNames } from "../../utils/classes";
import { ExpandableSectionContent } from "./ExpandableSectionContent";
import { ExpandableSectionContext } from "./ExpandableSectionContext";
import { ExpandableSectionHeader } from "./ExpandableSectionHeader";

import type { PropsWithChildren } from "react";

import * as styles from "./expandableSection.module.scss";

export interface ExpandableSectionProps {
	id?: string;
	expanded: boolean;
	onChange: (newState: boolean) => void;
	className?: string;
	withBorder?: boolean;
	testId?: string;
	keepMounted?: boolean;
}

type ExpandableSectionComponent = ((props: PropsWithChildren<ExpandableSectionProps>) => React.JSX.Element) & {
	Content: typeof ExpandableSectionContent;
	Header: typeof ExpandableSectionHeader;
	displayName?: string;
};

const ExpandableSectionRoot = (props: PropsWithChildren<ExpandableSectionProps>) => {
	const { id, expanded, onChange, className, withBorder, testId, children, keepMounted = false } = props;
	const context = useMemo(() => {
		return {
			expanded,
			keepMounted,
			toggle: () => onChange(!expanded),
		};
	}, [expanded, onChange, keepMounted]);

	return (
		<ExpandableSectionContext value={context}>
			<div id={id} className={mergeClassNames(className, withBorder && styles.border)} data-testid={testId}>
				{children}
			</div>
		</ExpandableSectionContext>
	);
};

export const ExpandableSection = ExpandableSectionRoot as ExpandableSectionComponent;

ExpandableSection.Content = ExpandableSectionContent;
ExpandableSection.Header = ExpandableSectionHeader;
ExpandableSection.displayName = "ExpandableSection";
